// src/components/doctor/ContextTabs.jsx
//
// The four tabs of the context drawer (Tier 3 — reference on demand):
//   AttentionTab  open action items, "Needs action now" first, text cleaned
//   ReportTab     the "Before the visit" briefing from the latest triage report
//   HistoryTab    earlier visits and signed prescriptions (copy medicines)
//   DocumentsTab  the patient's uploaded documents

import React, { useEffect, useMemo, useState } from 'react';
import DoctorDocumentsSection from './DoctorDocumentsSection';
import { groupOpenItems } from '../../utils/actionItems';
import { fmtDate } from '../../hooks/useConsultation';
import { describeItem, itemsFromPrescription } from '../../utils/prescription';

const LEVEL_BADGE = { CRITICAL: 'badge-critical', HIGH: 'badge-critical', MODERATE: 'badge-moderate', LOW: 'badge-low' };

// claudeKeyFindings is free text on some reports and a JSON list on others.
function findingsText(raw) {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map(f => (typeof f === 'string' ? f : f?.text || f?.finding || '')).filter(Boolean).join(' · ');
    }
  } catch { /* plain text */ }
  return String(raw);
}
const clip = (s, n = 320) => (s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);

/* ── Attention ───────────────────────────────────────────────────────────── */
function ItemRow({ row, onToggle }) {
  const { item, text, label, tier } = row;
  return (
    <li className="consult-item">
      <input type="checkbox" checked={!!item.completed} onChange={() => onToggle(item)} aria-label={`Mark done: ${text}`} />
      <div className="consult-item__body">
        <div className="consult-item__title">{text || 'Untitled item'}</div>
        <div className="consult-item__meta">
          <span className={`badge ${tier === 'now' ? 'badge-critical' : 'badge-unknown'}`}>{label}</span>
          {item.dueDate && <span>due {fmtDate(item.dueDate)}</span>}
        </div>
      </div>
    </li>
  );
}

export function AttentionTab({ context, onToggle }) {
  const [showRoutine, setShowRoutine] = useState(false);
  const groups = useMemo(() => groupOpenItems(context.items), [context.items]);
  if (context.status === 'loading') return <div className="consult-dsec"><div className="skeleton skeleton--row" /></div>;
  if (context.status === 'error') return <div className="consult-dsec"><div className="alert alert-error">Couldn’t load open items.</div></div>;
  return (
    <>
      <div className="consult-dsec">
        <h3 className="consult-dsec__h consult-dsec__h--now">Needs action now · {groups.now.length}</h3>
        {groups.now.length === 0 ? <div className="field-text">Nothing urgent is open.</div> : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {groups.now.map(r => <ItemRow key={r.item.id} row={r} onToggle={onToggle} />)}
          </ul>
        )}
      </div>
      <div className="consult-dsec">
        <h3 className="consult-dsec__h">
          <span>Routine · {groups.routine.length}</span>
          {groups.routine.length > 0 && (
            <button type="button" className="consult-link" aria-expanded={showRoutine} aria-controls="routine-items"
              onClick={() => setShowRoutine(s => !s)}>{showRoutine ? 'Hide' : 'Show'}</button>
          )}
        </h3>
        {groups.routine.length === 0 && <div className="field-text">Nothing outstanding.</div>}
        {showRoutine && (
          <ul id="routine-items" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {groups.routine.map(r => <ItemRow key={r.item.id} row={r} onToggle={onToggle} />)}
          </ul>
        )}
      </div>
    </>
  );
}

/* ── Report ──────────────────────────────────────────────────────────────── */
export function ReportTab({ context, onOpenRecord }) {
  if (context.status === 'loading') return <div className="consult-dsec"><div className="skeleton skeleton--row" /></div>;
  const [latest, previous] = context.reports;
  const delta = latest?.consensusRiskScore != null && previous?.consensusRiskScore != null
    ? latest.consensusRiskScore - previous.consensusRiskScore : null;
  const level = (latest?.consensusRiskLevel || '').toUpperCase();
  const findings = clip(findingsText(latest?.claudeKeyFindings));
  return (
    <div className="consult-dsec">
      <h3 className="consult-dsec__h">Before the visit</h3>
      {!latest ? <div className="field-text" style={{ marginBottom: 10 }}>No triage report yet — nothing to brief on.</div> : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <span className="stat-value" style={{ fontSize: 28 }}>{latest.consensusRiskScore ?? '—'}</span>
            {level && <span className={`badge ${LEVEL_BADGE[level] || 'badge-unknown'}`}>{level}</span>}
            <span className="card-row-meta">
              Report {fmtDate(latest.drawDate || latest.createdAt)}
              {delta != null && (delta === 0 ? ' · no change since previous' : ` · ${delta > 0 ? '▲' : '▼'} ${Math.abs(delta)} since previous`)}
            </span>
          </div>
          {findings && <div className="field-group"><div className="field-label">Key findings</div><div className="field-text">{findings}</div></div>}
          {latest.claudeRecommendation && (
            <div className="field-group"><div className="field-label">Recommendation</div><div className="field-text">{clip(String(latest.claudeRecommendation))}</div></div>
          )}
        </>
      )}
      <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenRecord}>Open full record →</button>
    </div>
  );
}

/* ── History ─────────────────────────────────────────────────────────────── */
export function HistoryTab({ history, loadHistory, previousRx, rxLoading, locked, onCopy }) {
  useEffect(() => { if (history.status === 'idle') loadHistory(); }, [history.status, loadHistory]);
  return (
    <>
      <div className="consult-dsec">
        <h3 className="consult-dsec__h">Last visits</h3>
        {(history.status === 'loading' || history.status === 'idle') && <div className="skeleton skeleton--row" />}
        {history.status === 'error' && (
          <div className="alert alert-error"><div className="grow">Couldn’t load earlier visits.</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={loadHistory}>Retry</button></div>
        )}
        {history.status === 'ready' && history.visits.length === 0 && <div className="field-text">No earlier visits on record.</div>}
        {history.status === 'ready' && history.visits.slice(0, 5).map(v => (
          <div key={v.id} className="consult-item">
            <div className="consult-item__body">
              <div className="consult-item__title">{v.diagnosisText || 'No diagnosis recorded'}</div>
              <div className="consult-item__meta">
                <span>{fmtDate(v.signedAt || v.createdAt)}</span>
                {v.doctorName && <span>· {v.doctorName}</span>}
                <span className={`badge ${v.status === 'SIGNED' ? 'badge-active' : 'badge-pending'}`}>{v.status === 'SIGNED' ? 'Signed' : 'Draft'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="consult-dsec">
        <h3 className="consult-dsec__h">Signed prescriptions</h3>
        {rxLoading && <div className="skeleton skeleton--row" />}
        {!rxLoading && previousRx.length === 0 && <div className="field-text">No previous prescriptions.</div>}
        {previousRx.map(rx => {
          const meds = itemsFromPrescription(rx);
          return (
            <div key={rx.id} className="consult-item" data-testid="prev-rx">
              <div className="consult-item__body">
                <div className="consult-item__title">{rx.rxNumber || 'Prescription'} · {fmtDate(rx.signedAt || rx.createdAt)}</div>
                <ul style={{ margin: '4px 0 6px', paddingLeft: 18 }}>
                  {meds.map(m => <li key={m.key} className="field-text">{describeItem(m)}</li>)}
                </ul>
                {meds.length > 0 && (
                  <button type="button" className="consult-link" disabled={locked} onClick={() => onCopy(rx)}>Copy medicines into this prescription</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ── Documents ───────────────────────────────────────────────────────────── */
export function DocumentsTab({ patientId, onOpenRecord }) {
  return (
    <div className="consult-dsec">
      <h3 className="consult-dsec__h"><span>Uploaded documents</span>
        <button type="button" className="consult-link" onClick={onOpenRecord}>Open full record</button></h3>
      <DoctorDocumentsSection patientId={patientId} />
    </div>
  );
}
